/**
 * Operations · Risks.
 */
import { useMemo, useState } from "react";
import {
  ShieldAlert, Sparkles, Truck, ShieldCheck, Users, Scale, PackageOpen, Coins,
} from "lucide-react";
import { Card, SectionHeader, TextInput, Button, SeverityBadge, ConfidenceBar } from "../../components/lib";
import { TabBar, Drawer, Field, Segmented } from "../../components/lib.overlay";
import { RecordEditor, FieldSpec } from "../../components/RecordEditor";
import { AuditTrail } from "../../components/AuditTrail";
import { RISKS, OpsRisk, RiskCategory, SUPPLIERS, BATCHES } from "../../mocks/operationsData";
import { PRODUCTS } from "../../mocks/commerceData";

const CATEGORY_META: Record<RiskCategory, { icon: any; label: string; tint: string; fg: string }> = {
  supply:     { icon: PackageOpen,  label: "Supply",     tint: "var(--ph-warning-tint)", fg: "var(--ph-warning)"       },
  quality:    { icon: ShieldCheck,  label: "Quality",    tint: "var(--ph-danger-tint)",  fg: "var(--ph-danger)"        },
  regulatory: { icon: Scale,        label: "Regulatory", tint: "var(--ph-ai-tint)",      fg: "var(--ph-ai)"            },
  hr:         { icon: Users,        label: "People",     tint: "var(--ph-brand-tint)",   fg: "var(--ph-brand-primary)" },
  logistics:  { icon: Truck,        label: "Logistics",  tint: "var(--ph-ai-tint)",      fg: "var(--ph-ai)"            },
  cash:       { icon: Coins,        label: "Cash",       tint: "var(--ph-success-tint)", fg: "var(--ph-success)"       },
};

const STATUS_META: Record<OpsRisk["status"], { bg: string; fg: string; label: string }> = {
  open:       { bg: "var(--ph-danger-tint)",  fg: "var(--ph-danger)",  label: "Open"       },
  monitoring: { bg: "var(--ph-warning-tint)", fg: "var(--ph-warning)", label: "Monitoring" },
  mitigated:  { bg: "var(--ph-success-tint)", fg: "var(--ph-success)", label: "Mitigated"  },
};

export default function Risks() {
  const [tab, setTab] = useState<OpsRisk["status"] | "all">("open");
  const [category, setCategory] = useState<RiskCategory | "all">("all");
  const [q, setQ]     = useState("");
  const [selected, setSelected] = useState<OpsRisk | null>(null);
  const [wsTab, setWsTab] = useState<"overview" | "related" | "actions" | "audit">("overview");
  const [editorOpen, setEditorOpen] = useState<"create" | "edit" | null>(null);

  const RISK_SCHEMA: FieldSpec[] = [
    { key: "title",             label: "Risk title",       kind: "text",     placeholder: "Supplier concentration on jasmine absolute" },
    { key: "category",          label: "Category",         kind: "select", options: [
        { value: "supply",     label: "Supply"     }, { value: "quality",  label: "Quality"  },
        { value: "regulatory", label: "Regulatory" }, { value: "hr",       label: "HR"       },
        { value: "logistics",  label: "Logistics"  }, { value: "cash",     label: "Cash"     },
    ]},
    { key: "severity",          label: "Severity",         kind: "select", options: [
        { value: "low",    label: "Low"    }, { value: "medium", label: "Medium" },
        { value: "high",   label: "High"   }, { value: "critical", label: "Critical" },
    ]},
    { key: "status",            label: "Status",           kind: "select", options: [
        { value: "open",       label: "Open"       },
        { value: "monitoring", label: "Monitoring" },
        { value: "mitigated",  label: "Mitigated"  },
        { value: "closed",     label: "Closed"     },
    ]},
    { key: "owner",             label: "Owner",            kind: "text",     placeholder: "Head of Ops" },
    { key: "likelihood",        label: "Probability",      kind: "number",   placeholder: "0.4" },
    { key: "impactEGP",         label: "Financial impact (EGP)", kind: "number", placeholder: "150000" },
    { key: "mitigationSummary", label: "Mitigation plan",  kind: "textarea", placeholder: "Diversify · 3 alternates identified" },
    { key: "dueRelative",       label: "Due date",         kind: "text",     placeholder: "in 14 days" },
    { key: "cause",             label: "Cause",            kind: "textarea", placeholder: "Root cause description" },
  ];

  const counts = useMemo(() => {
    const c: Record<string, number> = { all: RISKS.length };
    RISKS.forEach((r) => { c[r.status] = (c[r.status] || 0) + 1; });
    return c;
  }, []);

  const filtered = useMemo(
    () => RISKS.filter((r) =>
      (tab === "all" || r.status === tab) &&
      (category === "all" || r.category === category) &&
      (!q || r.title.toLowerCase().includes(q.toLowerCase()) || r.note.toLowerCase().includes(q.toLowerCase())),
    ),
    [tab, category, q],
  );

  const catCounts = useMemo(() => {
    const c: Record<string, number> = { all: RISKS.length };
    RISKS.forEach((r) => { c[r.category] = (c[r.category] || 0) + 1; });
    return c;
  }, []);

  return (
    <div className="space-y-6" data-testid="op-risks">
      <Card testId="op-risk-summary">
        <SectionHeader eyebrow="Register" title="Risk posture" action={<ShieldAlert size={14} style={{ color: "var(--ph-ink-muted)" }} />} />
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>Open</div>
            <div className="text-xl font-semibold tabular-nums mt-1" style={{ color: "var(--ph-danger)" }}>{counts.open || 0}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>Monitoring</div>
            <div className="text-xl font-semibold tabular-nums mt-1" style={{ color: "var(--ph-warning)" }}>{counts.monitoring || 0}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>Mitigated</div>
            <div className="text-xl font-semibold tabular-nums mt-1" style={{ color: "var(--ph-success)" }}>{counts.mitigated || 0}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>High severity</div>
            <div className="text-xl font-semibold tabular-nums mt-1" style={{ color: "var(--ph-ink-strong)" }}>
              {RISKS.filter((r) => r.severity === "high" && r.status !== "mitigated").length}
            </div>
          </div>
        </div>
      </Card>

      <Card testId="op-risk-filters">
        <div className="flex items-center gap-3">
          <div className="flex-1"><TextInput value={q} onChange={setQ} placeholder="Search risks…" testId="op-risk-search" /></div>
          <Button testId="op-risk-new">+ Log risk</Button>
        </div>
        <div className="mt-4 flex items-center gap-3 flex-wrap">
          <TabBar<OpsRisk["status"] | "all">
            testId="op-risk-tabs"
            active={tab}
            onChange={setTab}
            tabs={[
              { key: "open",       label: "Open",       count: counts.open       || 0 },
              { key: "monitoring", label: "Monitoring", count: counts.monitoring || 0 },
              { key: "mitigated",  label: "Mitigated",  count: counts.mitigated  || 0 },
              { key: "all",        label: "All",        count: counts.all             },
            ]}
          />
          <TabBar<RiskCategory | "all">
            testId="op-risk-cat"
            active={category}
            onChange={setCategory}
            tabs={[
              { key: "all",        label: "All"         },
              { key: "supply",     label: "Supply",     count: catCounts.supply     || 0 },
              { key: "quality",    label: "Quality",    count: catCounts.quality    || 0 },
              { key: "logistics",  label: "Logistics",  count: catCounts.logistics  || 0 },
              { key: "cash",       label: "Cash",       count: catCounts.cash       || 0 },
              { key: "regulatory", label: "Regulatory", count: catCounts.regulatory || 0 },
              { key: "hr",         label: "People",     count: catCounts.hr         || 0 },
            ]}
          />
        </div>
      </Card>

      <Card padded={false} testId="op-risk-list-card">
        <div className="p-5" style={{ borderBottom: "1px solid var(--ph-border-subtle)" }}>
          <SectionHeader eyebrow="Register" title={`${filtered.length} risk${filtered.length === 1 ? "" : "s"}`} />
        </div>
        <ul className="divide-y" style={{ borderColor: "var(--ph-border-subtle)" }} data-testid="op-risk-list">
          {filtered.map((r) => {
            const cat = CATEGORY_META[r.category];
            const Icon = cat.icon;
            const st = STATUS_META[r.status];
            return (
              <li key={r.id} data-testid={`op-risk-${r.id}`}
                className="p-4 flex items-center gap-3 cursor-pointer hover:bg-slate-50"
                onClick={() => setSelected(r)}>
                <span className="inline-flex items-center justify-center flex-shrink-0" style={{
                  width: 28, height: 28, borderRadius: "999px",
                  background: cat.tint, color: cat.fg,
                }}>
                  <Icon size={13} />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="text-sm font-semibold" style={{ color: "var(--ph-ink-strong)" }}>{r.title}</span>
                    <span className="px-1.5 py-0.5 text-[9px] font-semibold uppercase" style={{ letterSpacing: "0.14em", background: cat.tint, color: cat.fg, borderRadius: "var(--ph-radius-sm)" }}>
                      {cat.label}
                    </span>
                    <span className="px-1.5 py-0.5 text-[9px] font-semibold uppercase" style={{ letterSpacing: "0.14em", background: st.bg, color: st.fg, borderRadius: "var(--ph-radius-sm)" }}>
                      {st.label}
                    </span>
                  </div>
                  <div className="text-[11px] mt-0.5" style={{ color: "var(--ph-ink-muted)" }}>
                    Owner {r.owner} · Probability {r.probability} · <span className="inline-flex items-center gap-1" style={{ color: "var(--ph-ai)" }}><Sparkles size={9} /> AI {Math.round(r.aiConfidence * 100)}%</span>
                  </div>
                </div>
                <SeverityBadge severity={r.severity} />
              </li>
            );
          })}
        </ul>
      </Card>

      <Drawer open={Boolean(selected) && editorOpen === null} onClose={() => setSelected(null)} title="Risk 360" testId="op-risk-drawer" width={620}>
        {selected && (() => {
          const relatedSuppliers = SUPPLIERS.filter((s) => selected.category === "supply" && s.risk === "high");
          const relatedBatches   = BATCHES.filter((b) => selected.category === "quality" && b.stage !== "done").slice(0, 2);
          const relatedProducts  = PRODUCTS.filter((p) => selected.category === "regulatory" && p.status === "live").slice(0, 2);
          const impactEgp = (selected as any).impactEGP || (selected.severity === "high" ? 200000 : selected.severity === "medium" ? 75000 : 15000);
          const likelihood = (selected as any).likelihood || (selected.probability === "high" ? 0.6 : selected.probability === "medium" ? 0.4 : 0.2);
          return (
            <div>
              <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: CATEGORY_META[selected.category].fg }}>
                {CATEGORY_META[selected.category].label}
              </div>
              <div style={{ fontFamily: "var(--ph-display-font)", fontSize: "1.2rem", color: "var(--ph-ink-strong)" }} className="mt-1 mb-3">
                {selected.title}
              </div>

              <div className="mb-4">
                <Segmented<"overview" | "related" | "actions" | "audit">
                  testId="op-risk-ws-tabs"
                  value={wsTab}
                  onChange={setWsTab}
                  options={[
                    { value: "overview", label: "Overview" },
                    { value: "related",  label: "Related"  },
                    { value: "actions",  label: "Actions"  },
                    { value: "audit",    label: "Audit"    },
                  ]}
                />
              </div>

              {wsTab === "overview" && (
                <div data-testid="op-risk-ws-overview">
                  <Field label="Description" inline><span className="text-sm">{selected.title}</span></Field>
                  <Field label="Cause"       inline><span className="text-sm">{(selected as any).cause || "See mitigation note"}</span></Field>
                  <Field label="Impact"      inline><span className="text-sm">Business continuity, revenue, brand</span></Field>
                  <Field label="Financial impact" inline><span className="text-sm tabular-nums">EGP {impactEgp.toLocaleString()}</span></Field>
                  <Field label="Probability" inline><span className="text-sm tabular-nums">{Math.round(likelihood * 100)}%</span></Field>
                  <Field label="Severity"    inline><SeverityBadge severity={selected.severity} /></Field>
                  <Field label="Status" inline>
                    <span className="px-2 py-0.5 text-[10px] font-semibold uppercase" style={{ letterSpacing: "0.14em", background: STATUS_META[selected.status].bg, color: STATUS_META[selected.status].fg, borderRadius: "var(--ph-radius-sm)" }}>
                      {STATUS_META[selected.status].label}
                    </span>
                  </Field>
                  <Field label="Owner"       inline><span className="text-sm">{selected.owner}</span></Field>
                  <Field label="Due date"    inline><span className="text-sm">{(selected as any).dueRelative || "TBD"}</span></Field>
                  <Field label="AI confidence" inline>
                    <div style={{ width: 120 }}>
                      <div className="text-[11px] mb-1 tabular-nums" style={{ color: "var(--ph-ai)" }}>{Math.round(selected.aiConfidence * 100)}%</div>
                      <ConfidenceBar value={selected.aiConfidence} />
                    </div>
                  </Field>
                  <div className="pt-3">
                    <div className="text-[10px] uppercase tracking-[0.14em] mb-1.5" style={{ color: "var(--ph-ink-muted)" }}>Mitigation plan</div>
                    <div className="text-sm p-3" style={{ background: "var(--ph-surface)", borderRadius: "var(--ph-radius-md)", color: "var(--ph-ink-default)" }}>
                      {(selected as any).mitigationSummary || selected.note}
                    </div>
                  </div>
                </div>
              )}

              {wsTab === "related" && (
                <div data-testid="op-risk-ws-related">
                  <div className="text-[10px] uppercase tracking-[0.14em] mb-1" style={{ color: "var(--ph-ink-muted)" }}>Related supplier · {relatedSuppliers.length}</div>
                  {relatedSuppliers.map((s) => (
                    <div key={s.id} data-testid={`op-risk-sup-${s.id}`} className="py-2" style={{ borderBottom: "1px solid var(--ph-border-subtle)" }}>
                      <div className="text-sm font-medium" style={{ color: "var(--ph-ink-strong)" }}>{s.name}</div>
                      <div className="text-xs" style={{ color: "var(--ph-ink-muted)" }}>{s.city} · risk {s.risk}</div>
                    </div>
                  ))}
                  <div className="text-[10px] uppercase tracking-[0.14em] mb-1 mt-3" style={{ color: "var(--ph-ink-muted)" }}>Related product · {relatedProducts.length}</div>
                  {relatedProducts.map((p) => (
                    <div key={p.id} data-testid={`op-risk-prod-${p.id}`} className="py-2" style={{ borderBottom: "1px solid var(--ph-border-subtle)" }}>
                      <div className="text-sm font-medium" style={{ color: "var(--ph-ink-strong)" }}>{p.name}</div>
                      <div className="text-xs" style={{ color: "var(--ph-ink-muted)" }}>{p.sku} · {p.status}</div>
                    </div>
                  ))}
                  <div className="text-[10px] uppercase tracking-[0.14em] mb-1 mt-3" style={{ color: "var(--ph-ink-muted)" }}>Related batch · {relatedBatches.length}</div>
                  {relatedBatches.map((b) => (
                    <div key={b.id} data-testid={`op-risk-batch-${b.id}`} className="py-2" style={{ borderBottom: "1px solid var(--ph-border-subtle)" }}>
                      <div className="text-sm font-medium" style={{ color: "var(--ph-ink-strong)" }}>{b.code}</div>
                      <div className="text-xs" style={{ color: "var(--ph-ink-muted)" }}>{b.productName} · {b.stage}</div>
                    </div>
                  ))}
                  <div className="text-[10px] uppercase tracking-[0.14em] mb-1 mt-3" style={{ color: "var(--ph-ink-muted)" }}>Related decision / meeting / task</div>
                  <div className="text-sm p-2" style={{ background: "var(--ph-surface)", borderRadius: "var(--ph-radius-sm)", color: "var(--ph-ink-muted)" }}>Link via Governance module (Wave 4)</div>
                </div>
              )}

              {wsTab === "actions" && (
                <div data-testid="op-risk-ws-actions">
                  <div className="text-[10px] uppercase tracking-[0.14em] mb-1" style={{ color: "var(--ph-ink-muted)" }}>Comments</div>
                  <div className="text-sm p-2 mb-3" style={{ background: "var(--ph-surface)", borderRadius: "var(--ph-radius-sm)", color: "var(--ph-ink-muted)" }}>No comments yet.</div>
                  <div className="text-[10px] uppercase tracking-[0.14em] mb-1" style={{ color: "var(--ph-ink-muted)" }}>Attachments</div>
                  <div className="text-sm p-2 mb-3" style={{ background: "var(--ph-surface)", borderRadius: "var(--ph-radius-sm)", color: "var(--ph-ink-muted)" }}>No files attached.</div>
                  <div className="text-[10px] uppercase tracking-[0.14em] mb-1" style={{ color: "var(--ph-ink-muted)" }}>Timeline</div>
                  <AuditTrail collection="risks" recordId={selected.id} testId="op-risk-timeline" />
                </div>
              )}

              {wsTab === "audit" && (
                <div data-testid="op-risk-ws-audit">
                  <AuditTrail collection="risks" recordId={selected.id} testId="op-risk-audit" />
                </div>
              )}

              <div className="pt-4 flex justify-end gap-2">
                <Button variant="secondary" testId="op-risk-mitigate">Mitigate</Button>
                <Button variant="secondary" testId="op-risk-escalate">Escalate</Button>
                <Button variant="secondary" testId="op-risk-close">Close</Button>
                <Button testId="op-risk-edit" onClick={() => setEditorOpen("edit")}>Edit</Button>
              </div>
            </div>
          );
        })()}
      </Drawer>

      <RecordEditor<OpsRisk>
        open={editorOpen !== null}
        onClose={() => setEditorOpen(null)}
        mode={editorOpen === "edit" ? "edit" : "create"}
        title={editorOpen === "edit" ? "Edit risk" : "New risk"}
        collection="risks"
        schema={RISK_SCHEMA}
        initial={editorOpen === "edit" && selected ? (selected as unknown as Record<string, unknown>) : {}}
        onSaved={(row) => { if (editorOpen === "edit") setSelected(row as unknown as OpsRisk); }}
        testId="op-risk-editor"
      />
    </div>
  );
}
